import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { Users, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { WorkHoursChart, fmtMins } from './WorkHoursChart';

const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};
const shiftMonth = (ym, d) => {
  const [y, m] = ym.split('-').map(Number);
  const dt = new Date(y, m - 1 + d, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
};
const t = (iso) => (iso ? new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '—');
const d = (iso) => new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });

function Entries({ user }) {
  if (!user.entries.length) return <p className="text-[11px] text-muted-foreground">Keine Stempelzeiten in diesem Monat.</p>;
  return (
    <table className="w-full text-[11px]" data-testid={`team-hours-entries-${user.id}`}>
      <thead><tr className="text-left text-muted-foreground uppercase tracking-wider text-[9px]"><th className="py-1 font-normal">Tag</th><th className="py-1 font-normal">Clock-in</th><th className="py-1 font-normal">Clock-out</th><th className="py-1 font-normal text-right">Dauer</th></tr></thead>
      <tbody>
        {user.entries.map((e, i) => (
          <tr key={i} className="border-t border-border/50">
            <td className="py-1">{d(e.clock_in)}</td>
            <td className="py-1 font-mono">{t(e.clock_in)}</td>
            <td className="py-1 font-mono">{e.running ? <span className="text-emerald-400">läuft</span> : t(e.clock_out)}</td>
            <td className="py-1 font-mono text-right">{fmtMins(e.minutes)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UserHours({ user, month }) {
  const s = user.summary || {};
  const [y, m] = month.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const [showEntries, setShowEntries] = useState(false);
  return (
    <div className="bg-card border border-border rounded-sm p-5 space-y-4" data-testid={`team-hours-${user.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-sm font-mono">{user.name?.charAt(0)?.toUpperCase()}</div>
          <div>
            <div className="text-sm text-foreground">{user.name}</div>
            <div className="text-[10px] text-muted-foreground">Wochenziel {user.target_hours}h {user.clocked_in && <span className="text-emerald-400 ml-1">· gerade eingestempelt</span>}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="bg-secondary/50 border border-border/60 rounded-sm px-3 py-1.5 text-center" data-testid={`team-hours-month-${user.id}`}>
            <p className="text-sm font-mono text-foreground">{fmtMins(s.month_minutes)}</p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{monthLabel(month)} · Soll ≈ {Math.round(user.target_hours * daysInMonth / 7)}h</p>
          </div>
          <div className="bg-secondary/50 border border-border/60 rounded-sm px-3 py-1.5 text-center" data-testid={`team-hours-prev-${user.id}`}>
            <p className="text-sm font-mono text-muted-foreground">{fmtMins(user.prev_month?.minutes)}</p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Vormonat</p>
          </div>
        </div>
      </div>
      <WorkHoursChart weeks={s.weeks || []} targetHours={user.target_hours} height={130} />
      <button onClick={() => setShowEntries(v => !v)} className="text-[11px] text-blue-300 hover:text-blue-200" data-testid={`team-hours-toggle-entries-${user.id}`}>
        {showEntries ? 'Stempelzeiten ausblenden' : `Stempelzeiten anzeigen (${user.entries.length})`}
      </button>
      {showEntries && <Entries user={user} />}
    </div>
  );
}

// Colleagues' clocked hours released to me by an admin — hours only, no activity feed.
export function TeamHoursSection() {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (m) => {
    setLoading(true);
    try { const r = await api.getVisibleHours(m); setData(r.data); } catch { setData(null); }
    setLoading(false);
  }, []);
  useEffect(() => { load(month); }, [month, load]);

  if (!loading && (!data || !data.users.length)) return null;
  const cur = data?.month || month;
  return (
    <div className="space-y-3" data-testid="team-hours-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-medium text-foreground flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" /> Team-Stunden <span className="text-[10px] text-muted-foreground font-normal">nur gestempelte Zeit — keine Aktivitäten</span></h2>
        {cur && (
          <div className="flex items-center gap-1 text-[11px]">
            <button onClick={() => setMonth(shiftMonth(cur, -1))} className="p-1 rounded-sm border border-border hover:bg-secondary/40" data-testid="team-hours-prev-month"><ChevronLeft className="w-3 h-3" /></button>
            <span className="font-mono w-32 text-center" data-testid="team-hours-month-label">{monthLabel(cur)}</span>
            <button onClick={() => setMonth(shiftMonth(cur, 1))} disabled={data?.is_current} className="p-1 rounded-sm border border-border hover:bg-secondary/40 disabled:opacity-30" data-testid="team-hours-next-month"><ChevronRight className="w-3 h-3" /></button>
          </div>
        )}
      </div>
      {loading && !data ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : data.users.map(u => <UserHours key={u.id} user={u} month={cur} />)}
    </div>
  );
}
